import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Scan, Microscope, ShieldCheck, Sparkles, ChevronRight, BadgeCheck, Upload, FlaskConical,
  Crown, ArrowRight, ShieldAlert, ShieldX, Eye, Activity, HeartPulse, Target, Droplet, CheckCircle2,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

// Lightweight count-up for the hero score numbers (mobile-friendly, no deps).
const CountUp = ({ to, duration = 1100 }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf, start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      setVal(Math.round(p * to));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <>{val}</>;
};

const IMG = {
  athlete: "https://images.pexels.com/photos/9644820/pexels-photo-9644820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  lab: "https://images.pexels.com/photos/9243751/pexels-photo-9243751.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const C = {
  en: {
    heroTitleA: "Verify which supplements or herbs you really need,",
    heroTitleB: "not just brand's marketing",
    heroSub: "Scan the label. See what the evidence says.",
    verifiedSource: "verified — source: PubMed",
    heroFunctions: "Endurance, Longevity…",
    heroTaglineRest: "Verify which supplements or herbs you really need — not just brand's marketing",
    saPlan: "Annual plan",
    saTitle: "Superathlete Formula",
    saTagline: "Super Endurance & Longevity",
    saSub: "I share the natural Superathlete Formula I've researched — the nutrition that makes the real difference in the winningest athletes.",
    saUpdates: "New updates to the formula, released regularly.",
    saCta: "Unlock with the Annual plan",
    exampleTitle: "Example of a scan",
    condChips: ["Diabetes", "Inflammation", "Cardiovascular"],
    scanCta: "Scan a supplement",
    loginCta: "I already have an account",
    trust: ["PubMed evidence", "Interaction check", "Clean-label check", "WADA anti-doping"],
    poweredBy: "Research data from",
    wadaNote: "WADA is shown as an anti-doping reference status — always confirm each specific product with your anti-doping authority.",
    card: {
      scanned: "Scanned in 3s",
      evidence: "Evidence score",
      strongDesc: "Strong — backed by 1,200+ human studies",
      cleanDesc: "No fillers, preservatives or artificial additives",
      organsOk: "Low estimated load on kidney & liver",
      interactionNote: "Warns about blood-thinner combos (e.g., fish oil)",
      wadaRef: "WADA anti-doping — reference only",
    },
    safetyNote: "We analyze supplements against health research that is reviewed periodically and can change. A safe result is not guaranteed. Always tell your doctor before taking any supplements or combining it with medication, especially if you have a medical condition.",
    howTitle: "From label to evidence in seconds",
    how: [
      { icon: Upload, t: "Snap the label", d: "Upload or photograph any supplement facts panel." },
      { icon: Microscope, t: "See the science", d: "AI grades every active ingredient against real PubMed studies." },
      { icon: ShieldCheck, t: "Check anti-doping", d: "See each ingredient's WADA reference status before you compete — always confirm with your authority." },
    ],
    featTitle: "Everything a serious athlete needs",
    feats: [
      { icon: Scan, t: "Label Scanner", d: "Instant AI breakdown of actives vs fillers with evidence grades A–D." },
      { icon: Microscope, t: "Live PubMed", d: "Real literature searches summarized neutrally — no hype." },
      { icon: ShieldCheck, t: "Anti-Doping Reference", d: "Each ingredient may be flagged against the WADA list as an educational reference — not a real-time guarantee." },
      { icon: Sparkles, t: "Superathlete Formula", d: "Optimize your stack: overlaps, gaps and perfect timing." },
    ],
    adTitle: "Is it anti-doping safe?",
    adSub: "For every nutrient we show a curated anti-doping reference — based on public information and research — as a clear starting point before you compete. Always confirm the specific product with your anti-doping authority. Four clear levels:",
    adLevels: [
      { icon: ShieldCheck, t: "Safe", d: "No prohibited substances; supported by research.", cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
      { icon: Eye, t: "Monitored", d: "On the WADA Monitoring Program — use with awareness.", cls: "text-blue-600 bg-blue-50 border-blue-200" },
      { icon: ShieldAlert, t: "Caution", d: "Contamination risk or unclear status — verify the batch.", cls: "text-amber-600 bg-amber-50 border-amber-200" },
      { icon: ShieldX, t: "Prohibited", d: "Banned in sport — do not use if you compete.", cls: "text-red-600 bg-red-50 border-red-200" },
    ],
    adCta: "Explore the ingredient library",
    adDisclaimer: "Not a guarantee: we cannot confirm in real time whether every ingredient is on WADA's official Prohibited List, which can change. These statuses are an educational reference — verify each specific product with your anti-doping authority before competing.",
    gradeTitle: "Evidence grades you can trust",
    gradeSub: "Every ingredient is rated by the strength of human research behind it.",
    grades: [
      { g: "A", t: "Strong", d: "Robust human trials & meta-analyses" },
      { g: "B", t: "Moderate", d: "Promising but limited human data" },
      { g: "C", t: "Inconclusive", d: "Mixed or preliminary findings" },
      { g: "D", t: "Caution", d: "Little evidence or safety concerns" },
    ],
    nutrientsTitle: "Browse the most-researched nutrients",
    nutrientsCta: "Open the library",
    condTitle: "Evidence by health condition",
    condSub: "Discover which nutrients, herbs and natural products were studied in PubMed for any health condition, with the doses used and evidence from experiments and research found in PubMed.",
    condCta: "Explore conditions",
    priceBand: "Go Premium for full PubMed evidence, condition research and the Superathlete Formula.",
    priceCta: "View plans",
    faqTitle: "Frequent questions",
    faqs: [
      ["Does Suppev give medical advice?", "No. Suppev is an educational platform that summarizes scientific evidence. It does not diagnose, treat, prevent or cure any disease. Always consult a healthcare professional."],
      ["How do you know if a supplement is anti-doping safe?", "We show a curated anti-doping reference status (Safe / Monitored / Caution / Prohibited) based on publicly available information and research — it is educational, not an official WADA ruling. Always confirm the specific product with your anti-doping authority before competing."],
      ["Where does the evidence come from?", "Live searches of PubMed / NCBI, summarized neutrally by AI, plus a curated ingredient library graded A–D by the strength of human research. Evidence grades are AI-assisted and educational — not a ruling by any regulator."],
      ["What do I get with Premium?", "Full PubMed evidence & studies, evidence by health condition, expert dosing, and the Superathlete Formula stack optimizer. $9.99/month or $59.99/year, cancel anytime."],
    ],
  },
  es: {
    heroTitleA: "Verifica qué suplementos o hierbas realmente necesitas,",
    heroTitleB: "no solo el marketing de las marcas",
    heroSub: "Escanea la etiqueta. Mira qué dice la evidencia.",
    verifiedSource: "verified — source: PubMed",
    heroFunctions: "Resistencia, Longevidad…",
    heroTaglineRest: "Verifica qué suplementos o hierbas realmente necesitas — no solo el marketing de las marcas",
    saPlan: "Plan anual",
    saTitle: "Fórmula del Superatleta",
    saTagline: "Súper resistencia y longevidad",
    saSub: "Comparto la fórmula natural del Superatleta que he investigado: qué nutrición marca la diferencia real en los atletas más ganadores.",
    saUpdates: "Publico nuevas actualizaciones de la fórmula con regularidad.",
    saCta: "Desbloquear con el plan Anual",
    exampleTitle: "Ejemplo de un escaneo",
    condChips: ["Diabetes", "Inflamación", "Cardiovascular"],
    scanCta: "Escanear un suplemento",
    loginCta: "Ya tengo una cuenta",
    trust: ["Evidencia PubMed", "Chequeo de interacciones", "Etiqueta limpia", "Antidopaje WADA"],
    poweredBy: "Datos de investigación de",
    wadaNote: "WADA se muestra como estado antidopaje de referencia — confirma siempre cada producto concreto con tu autoridad antidopaje.",
    card: {
      scanned: "Escaneado en 3s",
      evidence: "Puntuación de evidencia",
      strongDesc: "Fuerte — respaldado por más de 1.200 estudios en humanos",
      cleanDesc: "Sin rellenos, conservantes ni aditivos artificiales",
      organsOk: "Carga estimada baja en riñón e hígado",
      interactionNote: "Avisa de combinaciones con anticoagulantes (p. ej. omega-3)",
      wadaRef: "Antidopaje WADA — solo referencia",
    },
    safetyNote: "Analizamos los suplementos con base en investigación de salud que se revisa periódicamente y puede cambiar. Un resultado seguro no está garantizado. Informa siempre a tu médico antes de tomar cualquier suplemento o combinarlo con algún medicamento, especialmente si tienes alguna enfermedad.",
    howTitle: "De la etiqueta a la evidencia en segundos",
    how: [
      { icon: Upload, t: "Captura la etiqueta", d: "Sube o fotografía cualquier panel de información del suplemento." },
      { icon: Microscope, t: "Mira la ciencia", d: "La IA califica cada ingrediente activo con estudios reales de PubMed." },
      { icon: ShieldCheck, t: "Verifica el antidopaje", d: "Consulta el estado WADA de referencia de cada ingrediente antes de competir — confirma siempre con tu autoridad." },
    ],
    featTitle: "Todo lo que un atleta serio necesita",
    feats: [
      { icon: Scan, t: "Escáner de Etiquetas", d: "Desglose instantáneo de activos vs relleno con nivel de evidencia A–D." },
      { icon: Microscope, t: "PubMed en Vivo", d: "Búsquedas reales resumidas de forma neutral — sin exageraciones." },
      { icon: ShieldCheck, t: "Referencia Antidopaje", d: "Cada ingrediente puede señalarse frente a la lista de la WADA como referencia educativa — no una garantía en tiempo real." },
      { icon: Sparkles, t: "Fórmula del Superatleta", d: "Optimiza tu stack: solapamientos, carencias y horarios perfectos." },
    ],
    adTitle: "¿Es seguro para el antidopaje?",
    adSub: "Para cada nutriente mostramos una referencia antidopaje curada — basada en información pública e investigación — como punto de partida claro antes de competir. Confirma siempre el producto concreto con tu autoridad antidopaje. Cuatro niveles claros:",
    adLevels: [
      { icon: ShieldCheck, t: "Seguro", d: "Sin sustancias prohibidas; respaldado por investigación.", cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
      { icon: Eye, t: "Monitorizado", d: "En el Programa de Monitoreo de la WADA — úsalo con conocimiento.", cls: "text-blue-600 bg-blue-50 border-blue-200" },
      { icon: ShieldAlert, t: "Precaución", d: "Riesgo de contaminación o estado poco claro — verifica el lote.", cls: "text-amber-600 bg-amber-50 border-amber-200" },
      { icon: ShieldX, t: "Prohibido", d: "Prohibido en el deporte — no lo uses si compites.", cls: "text-red-600 bg-red-50 border-red-200" },
    ],
    adCta: "Explorar la biblioteca de ingredientes",
    adDisclaimer: "No es una garantía: no podemos confirmar en tiempo real si cada ingrediente está en la Lista de Prohibiciones oficial de la WADA, que puede cambiar. Estos estados son una referencia educativa — verifica cada producto concreto con tu autoridad antidopaje antes de competir.",
    gradeTitle: "Niveles de evidencia en los que confiar",
    gradeSub: "Cada ingrediente se califica según la solidez de la investigación en humanos.",
    grades: [
      { g: "A", t: "Fuerte", d: "Ensayos en humanos y metaanálisis sólidos" },
      { g: "B", t: "Moderado", d: "Datos en humanos prometedores pero limitados" },
      { g: "C", t: "No concluyente", d: "Hallazgos mixtos o preliminares" },
      { g: "D", t: "Precaución", d: "Poca evidencia o dudas de seguridad" },
    ],
    nutrientsTitle: "Explora los nutrientes más investigados",
    nutrientsCta: "Abrir la biblioteca",
    condTitle: "Evidencia por condición de salud",
    condSub: "Descubre qué nutrientes, hierbas y productos naturales se estudiaron en PubMed para alguna condición de salud, con las dosis usadas y evidencia de experimentos e investigaciones encontradas en PubMed.",
    condCta: "Explorar condiciones",
    priceBand: "Hazte Premium para la evidencia completa de PubMed, la evidencia por condición de salud y la Fórmula del Superatleta.",
    priceCta: "Ver planes",
    faqTitle: "Preguntas frecuentes",
    faqs: [
      ["¿Suppev da consejo médico?", "No. Suppev es una plataforma educativa que resume la evidencia científica. No diagnostica, trata, previene ni cura ninguna enfermedad. Consulta siempre a un profesional de la salud."],
      ["¿Cómo saben si un suplemento es seguro para el antidopaje?", "Mostramos un estado antidopaje de referencia (Seguro / Monitorizado / Precaución / Prohibido) basado en información pública e investigación — es educativo, no un dictamen oficial de la WADA. Confirma siempre el producto concreto con tu autoridad antidopaje antes de competir."],
      ["¿De dónde viene la evidencia?", "Búsquedas en vivo en PubMed / NCBI, resumidas de forma neutral por IA, más una biblioteca de ingredientes calificada A–D según la solidez de la investigación en humanos. Los niveles de evidencia son asistidos por IA y educativos — no un dictamen de ningún regulador."],
      ["¿Qué incluye Premium?", "Evidencia y estudios completos de PubMed, evidencia por condición de salud, dosis por experto y el optimizador de stack Fórmula del Superatleta. $9.99/mes o $59.99/año, cancela cuando quieras."],
    ],
  },
};

const NUTRIENTS = ["Creatine", "Whey Protein", "Beta-Alanine", "Caffeine", "Citrulline", "Ashwagandha", "Beetroot", "Omega-3", "Vitamin D3", "BCAAs", "Magnesium", "L-Theanine"];
const NUTRIENTS_B = ["Zinc", "Electrolytes", "Collagen", "Glutamine", "Tongkat Ali", "Rhodiola", "Taurine", "L-Tyrosine", "Iron", "Probiotics", "Turmeric", "EAAs"];
const NutrientChip = ({ n }) => (
  <span className="whitespace-nowrap rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700">
    {n}
  </span>
);
const GRADE_CLR = { A: "bg-emerald-500", B: "bg-blue-500", C: "bg-amber-500", D: "bg-red-500" };

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay }} className={className}>
    {children}
  </motion.div>
);

const ConditionExplorer = ({ c }) => {
  return (
    <Reveal>
      <section data-testid="hero-research-card" className="mt-8 grid overflow-hidden rounded-3xl border-2 border-emerald-200 bg-white shadow-xl lg:grid-cols-[1.4fr_1fr]">
        <div className="p-6 sm:p-8">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700"><HeartPulse size={13} /> PubMed</span>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{c.condTitle}</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600">{c.condSub}</p>
          <Link to="/evidence" data-testid="hero-research-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-emerald-600">{c.condCta} <ArrowRight size={16} /></Link>
        </div>
        <div className="relative hidden min-h-[280px] lg:block">
          <img src={IMG.lab} alt="research lab" loading="lazy" className="absolute inset-0 h-full w-full object-cover object-[center_30%]" />
        </div>
      </section>
    </Reveal>
  );
};

export default function Landing() {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const c = C[lang] || C.en;

  return (
    <div className="-mt-2">
      {/* HERO */}
      <section className="pt-4 lg:pt-8">
        <h1 className="max-w-5xl text-4xl font-extrabold leading-[1.05] tracking-tight text-gray-900 sm:text-5xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {c.heroTitleA} <span className="text-emerald-500">{c.heroTitleB}</span>
        </h1>
      </section>

      {/* PRIORITY: Evidence by health condition */}
      <ConditionExplorer c={c} />

      {/* SCANNER */}
      <section className="mt-12" data-testid="hero-scanner-cta-section">
        <div className="flex flex-col gap-3 sm:flex-row">
          <button data-testid="landing-scan-cta-button" onClick={() => navigate("/scan")}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-7 py-4 text-base font-bold text-white shadow-sm transition-colors hover:bg-emerald-600">
            <Scan size={19} /> {c.scanCta}
            <ChevronRight size={18} className="transition-transform group-hover:translate-x-0.5" />
          </button>
          <button data-testid="landing-login-cta-button" onClick={() => navigate(user ? "/scan" : "/login")}
            className="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white px-7 py-4 text-base font-semibold text-gray-800 transition-colors hover:bg-gray-50">
            {c.loginCta}
          </button>
        </div>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-gray-600">{c.heroSub}</p>
        <p data-testid="hero-verified-source" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600">
          <BadgeCheck size={15} /> {c.verifiedSource}
        </p>
      </section>

      {/* PRODUCT EXAMPLE — Creatine Monohydrate */}
      <section data-testid="product-example-section" className="mt-12">
        <Reveal className="mb-6 text-center">
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{c.exampleTitle}</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="relative mx-auto max-w-md">
            <div className="absolute -right-4 -top-4 -z-10 h-40 w-40 rounded-full bg-emerald-200/50 blur-3xl" />
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600"><FlaskConical size={22} /></div>
                  <div>
                    <p className="font-bold text-gray-800">Creatine Monohydrate</p>
                    <p className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><Scan size={11} /> {c.card.scanned}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">Grade A</span>
                </div>
              </div>

              <div className="mt-5 rounded-2xl bg-emerald-50 p-4">
                <div className="flex items-end justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">{c.card.evidence}</span>
                  <span className="text-3xl font-extrabold leading-none text-emerald-600"><CountUp to={94} /><span className="text-base text-emerald-400">/100</span></span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-emerald-200">
                  <motion.div initial={{ width: 0 }} whileInView={{ width: "94%" }} viewport={{ once: true }} transition={{ duration: 0.9, ease: "easeOut" }} className="h-full rounded-full bg-emerald-500" />
                </div>
                <p className="mt-1.5 text-xs font-medium text-emerald-700">{c.card.strongDesc}</p>
              </div>

              <div className="mt-4 space-y-2.5">
                <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-3">
                  <Sparkles size={16} className="shrink-0 text-emerald-500" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800">{t("scan.cleanLabelTitle")}</p>
                    <p className="text-xs text-gray-500">{c.card.cleanDesc}</p>
                  </div>
                  <CheckCircle2 size={18} className="shrink-0 text-emerald-500" />
                </div>

                <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-3">
                  <Droplet size={16} className="shrink-0 text-emerald-500" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800">{t("scan.kidney")} · {t("scan.liver")}</p>
                    <p className="text-xs text-gray-500">{c.card.organsOk}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-600">{t("scan.low")}</span>
                </div>

                <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-3">
                  <Target size={16} className="shrink-0 text-emerald-500" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800">{t("scan.goalFitTitle")} · {t("goal.strength")}</p>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-100">
                      <motion.div initial={{ width: 0 }} whileInView={{ width: "97%" }} viewport={{ once: true }} transition={{ duration: 1, ease: "easeOut", delay: 0.2 }} className="h-full rounded-full bg-emerald-500" />
                    </div>
                  </div>
                  <span className="text-sm font-extrabold text-emerald-600"><CountUp to={97} /></span>
                </div>

                <div className="flex items-center gap-2 pt-1 text-xs font-medium text-gray-400">
                  <ShieldCheck size={13} className="shrink-0 text-gray-400" /> {c.card.wadaRef}
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-3">
              <ShieldAlert size={16} className="mt-0.5 shrink-0 text-amber-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800">{t("scan.interactionsTitle")}</p>
                <p className="text-xs text-amber-700">{c.card.interactionNote}</p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* SUPERATHLETE FORMULA — annual-only perk */}
      <section data-testid="superathlete-formula-section" className="mt-12 overflow-hidden rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
        <div className="grid items-center gap-8 p-8 sm:p-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-3 py-1 text-xs font-bold text-white"><Crown size={13} /> {c.saPlan}</span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.saTitle}</h2>
            <p className="mt-1 text-lg font-bold text-emerald-600 sm:text-xl">{c.saTagline}</p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-gray-700">{c.saSub}</p>
            <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><Sparkles size={15} /> {c.saUpdates}</p>
            <button onClick={() => navigate("/pricing")} data-testid="superathlete-cta-button" className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-emerald-600">{c.saCta} <ArrowRight size={16} /></button>
          </div>
          <div className="relative mx-auto w-full max-w-sm">
            <div className="overflow-hidden rounded-3xl shadow-xl">
              <div className="relative aspect-[4/5]">
                <img src={IMG.athlete} alt="superathlete" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/70 via-emerald-900/10 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-emerald-200">Superathlete</p>
                  <p className="text-2xl font-extrabold leading-tight text-white">{c.saTagline}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mt-16">
        <Reveal><h2 className="mb-8 text-center text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{c.howTitle}</h2></Reveal>
        <div className="grid gap-4 md:grid-cols-3">
          {c.how.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="relative rounded-2xl border border-gray-200 bg-white p-6">
              <span className="absolute right-5 top-5 text-3xl font-extrabold text-gray-100">{i + 1}</span>
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><s.icon size={22} /></div>
              <h3 className="mb-1.5 text-lg font-bold text-gray-800">{s.t}</h3>
              <p className="text-sm leading-relaxed text-gray-600">{s.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* EVIDENCE GRADES */}
      <section className="mt-16">
        <Reveal className="mb-8 text-center">
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{c.gradeTitle}</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-gray-600">{c.gradeSub}</p>
        </Reveal>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {c.grades.map((g, i) => (
            <Reveal key={i} delay={i * 0.06} className="rounded-2xl border border-gray-200 bg-white p-6">
              <div className={`mb-3 flex h-11 w-11 items-center justify-center rounded-xl text-lg font-extrabold text-white ${GRADE_CLR[g.g]}`}>{g.g}</div>
              <h3 className="font-bold text-gray-800">{g.t}</h3>
              <p className="mt-1 text-sm leading-relaxed text-gray-600">{g.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* NUTRIENTS */}
      <section className="mt-16 rounded-3xl border border-gray-200 bg-white p-8 sm:p-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{c.nutrientsTitle}</h2>
          <Link to="/evidence" className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-600 hover:underline">{c.nutrientsCta} <ArrowRight size={15} /></Link>
        </div>
        <div className="space-y-3">
          <div className="marquee">
            <div className="marquee__track">
              {[...NUTRIENTS, ...NUTRIENTS].map((n, i) => <NutrientChip key={`a-${i}`} n={n} />)}
            </div>
          </div>
          <div className="marquee">
            <div className="marquee__track marquee__track--reverse">
              {[...NUTRIENTS_B, ...NUTRIENTS_B].map((n, i) => <NutrientChip key={`b-${i}`} n={n} />)}
            </div>
          </div>
        </div>
      </section>

      {/* PRICE BAND */}
      <section className="mt-16 rounded-3xl bg-emerald-900 p-8 text-center sm:p-12">
        <Crown className="mx-auto mb-3 text-amber-300" size={30} />
        <p className="mx-auto max-w-2xl text-xl font-bold text-white sm:text-2xl">{c.priceBand}</p>
        <button onClick={() => navigate("/pricing")} className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-emerald-700 transition-transform hover:scale-[1.02]">
          {c.priceCta} <ArrowRight size={16} />
        </button>
      </section>

      {/* FAQ */}
      <section className="mt-16">
        <Reveal><h2 className="mb-6 text-center text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{c.faqTitle}</h2></Reveal>
        <div className="mx-auto max-w-2xl rounded-2xl border border-gray-200 bg-white px-6">
          <Accordion type="single" collapsible>
            {c.faqs.map(([q, a], i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left text-sm font-bold text-gray-800">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-gray-600">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL DISCLAIMER */}
      <section className="mt-14 mb-4">
        <p data-testid="landing-final-disclaimer" className="mx-auto max-w-3xl text-center text-xs leading-relaxed text-gray-400">
          {c.safetyNote}
        </p>
      </section>
    </div>
  );
}
